/**
 * useTransmittalPermissions
 *
 * Platform-agnostic hook for deriving transmittal-related permissions.
 * Checks module access, seat status, and feature permissions.
 *
 * Usage:
 * - Web: Used in ListTransmittals, TransmitalDetails
 * - Mobile: Used in transmittal screens for permission gating
 */

import { useMemo } from "react";
import { UseTransmittalPermissionsParams } from "./transmittal.types";
import _ from "lodash";

// Portal seat constant (imported from helpers in actual usage)
const PORTAL_NO_SEAT = "PORTAL_NO_SEAT";

interface UseTransmittalPermissionsReturn {
  // Status
  isReady: boolean;

  // Permissions
  hasPortalSeat: boolean;
  canViewModule: boolean;
  canCreateTransmittal: boolean;
  canViewTransmittalList: boolean;
}

function useTransmittalPermissions({
  moduleAccess,
  byPortal,
}: UseTransmittalPermissionsParams): UseTransmittalPermissionsReturn {
  // Status: Module access is ready
  const isReady = useMemo(() => {
    return byPortal ? true : moduleAccess?.ready === true;
  }, [moduleAccess?.ready, byPortal]);

  // Permission: User has a valid portal seat
  const hasPortalSeat = useMemo(() => {
    return byPortal ? true : moduleAccess?.portalSeat !== PORTAL_NO_SEAT;
  }, [moduleAccess?.portalSeat, byPortal]);

  // Permission: Can view transmittal module
  const canViewModule = true;

  // Permission: Can create transmittals
  const canCreateTransmittal = true;

  // Permission: Can view transmittal list (combines seat + module visibility)
  const canViewTransmittalList = useMemo(() => {
    return hasPortalSeat && canViewModule;
  }, [hasPortalSeat, canViewModule]);

  return {
    isReady,
    hasPortalSeat,
    canViewModule,
    canCreateTransmittal,
    canViewTransmittalList,
  };
}

export default useTransmittalPermissions;
